import React from 'react';
import { useMenuIcon } from '../../../hooks/useMenuIcon';
import { useBatchManagement } from '../../../hooks/useBatchManagement';
import { BatchDashboard } from './BatchDashboard';
import { BatchJobCard } from './BatchJobCard';
import { BatchHistoryTable } from './BatchHistoryTable';
import { BatchConfigManager } from './BatchConfigManager';
import { BatchHistoryModal } from './BatchHistoryModal';
import { BatchJobType, BATCH_JOB_NAMES } from '../../../types/batch';

export const BatchManagement: React.FC = () => {
  const menuIcon = useMenuIcon();
  const {
    batchJobs,
    statistics,
    runningJobs,
    isLoading,
    error,
    refreshAll,
    executeManually
  } = useBatchManagement();

  const [selectedJobType, setSelectedJobType] = React.useState<BatchJobType | null>(null);

  if (isLoading && batchJobs.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <span className="text-red-600">❌</span>
            <span className="text-red-800 font-medium">에러 발생</span>
          </div>
          <div className="mt-2 text-red-700">{error}</div>
          <button
            onClick={refreshAll}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* 페이지 제목 */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          {menuIcon ? <span className="text-4xl">{menuIcon}</span> : '📊'} 배치 작업 관리
        </h1>
        <p className="text-gray-600 mt-2">배치 작업의 실행 상태를 모니터링하고 수동으로 실행할 수 있습니다.</p>
      </div>

      {/* 배치 설정 관리 */}
      <BatchConfigManager />

      {/* 대시보드 섹션 */}
      <BatchDashboard 
        statistics={statistics}
        runningCount={runningJobs.length}
        onRefresh={refreshAll}
      />

      {/* 배치 작업 목록 */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">📋 배치 작업 목록</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {batchJobs.map((job) => (
            <BatchJobCard
              key={job.jobType}
              job={job}
              onExecute={executeManually}
              onViewHistory={(jobType) => setSelectedJobType(jobType)}
            />
          ))}
        </div>
      </div>

      {/* 실행 이력 테이블 */}
      <BatchHistoryTable />

      {/* 실행 이력 모달 */}
      {selectedJobType && (
        <BatchHistoryModal
          jobType={selectedJobType}
          displayName={BATCH_JOB_NAMES[selectedJobType] || selectedJobType}
          onClose={() => setSelectedJobType(null)}
        />
      )}
    </div>
  );
};
